/* Created by Interactive 63 - https://interactive63.com */
/**
 * Pure helpers that turn the element table into the flat list of rows shown in the Layers
 * panel, honouring expansion and search.
 */
import type { MoveTarget } from '../../core/import/layers';
import { CONTAINER_TAGS, type ElementId, type ElementMeta } from '../../core/model/types';

/** ID of the synthetic group that holds gradient stops. */
export const GRADIENTS_ID = '__gradients';

export interface LayerRow {
  id: string;
  depth: number;
  /** Absent for the synthetic gradients group. */
  element?: ElementMeta;
  hasChildren: boolean;
  expanded: boolean;
}

export interface RowSource {
  elements: Record<ElementId, ElementMeta>;
  rootIds: readonly ElementId[];
}

/** Returns the IDs of an element's ancestors, nearest first. */
export function ancestorsOf(elements: Record<ElementId, ElementMeta>, id: ElementId): ElementId[] {
  const result: ElementId[] = [];
  const seen = new Set<ElementId>([id]);
  for (
    let parent = elements[id]?.parentId;
    parent && !seen.has(parent);
    parent = elements[parent]?.parentId
  ) {
    seen.add(parent);
    result.push(parent);
  }
  return result;
}

/**
 * Builds the visible rows in display order: front to back, so the layer drawn on top is the top
 * row, and a group's contents are listed the same way. With a search query, only matching elements and
 * their ancestors appear, and matching branches are shown expanded.
 */
export function buildRows(
  source: RowSource,
  expanded: ReadonlySet<string>,
  query: string,
): LayerRow[] {
  const { elements, rootIds } = source;
  const needle = query.trim().toLowerCase();
  const searching = needle !== '';
  const stops = Object.values(elements).filter((e) => e.tag === 'stop');

  const matches = new Map<ElementId, boolean>();
  /** Whether an element or any descendant matches the query. */
  const included = (id: ElementId): boolean => {
    const cached = matches.get(id);
    if (cached !== undefined) return cached;
    const element = elements[id];
    const result =
      !searching ||
      (element !== undefined &&
        (element.label.toLowerCase().includes(needle) || element.childIds.some(included)));
    matches.set(id, result);
    return result;
  };

  const rows: LayerRow[] = [];
  /** Appends an element row and, if open, its children. */
  const visit = (id: ElementId, depth: number): void => {
    const element = elements[id];
    if (!element || !included(id)) return;
    const children = element.childIds.filter(included).reverse();
    const open = children.length > 0 && (searching || expanded.has(id));
    rows.push({ id, depth, element, hasChildren: element.childIds.length > 0, expanded: open });
    if (open) for (const child of children) visit(child, depth + 1);
  };
  for (const id of [...rootIds].reverse()) visit(id, 0);

  const visibleStops = stops.filter((stop) => included(stop.id));
  if (visibleStops.length > 0) {
    const open = searching || expanded.has(GRADIENTS_ID);
    rows.push({ id: GRADIENTS_ID, depth: 0, hasChildren: true, expanded: open });
    if (open) {
      for (const stop of visibleStops) {
        rows.push({ id: stop.id, depth: 1, element: stop, hasChildren: false, expanded: false });
      }
    }
  }
  return rows;
}

/** Returns the selectable IDs between two rows, inclusive, in display order. */
export function rangeBetween(rows: readonly LayerRow[], fromId: string, toId: string): ElementId[] {
  const from = rows.findIndex((r) => r.id === fromId);
  const to = rows.findIndex((r) => r.id === toId);
  if (from === -1 || to === -1) return [];
  const [start, end] = from < to ? [from, to] : [to, from];
  return rows
    .slice(start, end + 1)
    .filter((r) => r.element !== undefined)
    .map((r) => r.id);
}

export type DropPosition = 'before' | 'after' | 'inside';

export interface DropResult {
  target: MoveTarget;
  /** The row the drop indicator belongs to. */
  rowId: string;
  position: DropPosition;
}

/** Share of a group row's height at each edge that means before or after rather than inside. */
const GROUP_EDGE_SHARE = 0.25;

/**
 * Works out where dragged layers would land if released over a row. `fraction` is how far down
 * the row the pointer is (0 to 1); the top and bottom quarter of a group row place the layers
 * beside it and the middle places them inside. The list runs front to back while the data runs
 * back to front, so above a row means after it in the data. Returns null over rows that accept
 * nothing, or when a group would end up inside itself.
 */
export function resolveDrop(
  source: RowSource,
  rows: readonly LayerRow[],
  rowIndex: number,
  fraction: number,
  dragged: readonly ElementId[],
): DropResult | null {
  const { elements, rootIds } = source;
  /** The ordered children of a parent, or the top level for null. */
  const listOf = (parentId: ElementId | null): readonly ElementId[] =>
    parentId ? (elements[parentId]?.childIds ?? []) : rootIds;
  /** Builds the result, with the index counted after the dragged layers have left their places. */
  const finish = (
    parentId: ElementId | null,
    rawIndex: number,
    rowId: string,
    position: DropPosition,
  ): DropResult | null => {
    if (
      parentId &&
      [parentId, ...ancestorsOf(elements, parentId)].some((id) => dragged.includes(id))
    ) {
      return null;
    }
    const leaving = listOf(parentId)
      .slice(0, rawIndex)
      .filter((id) => dragged.includes(id)).length;
    return { target: { parentId, index: rawIndex - leaving }, rowId, position };
  };

  const last = rows[rows.length - 1];
  if (rowIndex >= rows.length) {
    return last?.element && last.element.tag !== 'stop' ? finish(null, 0, last.id, 'after') : null;
  }
  const row = rows[Math.max(0, rowIndex)];
  const meta = row?.element;
  if (!row || !meta || meta.tag === 'stop') return null;

  const siblings = listOf(meta.parentId);
  if (!CONTAINER_TAGS.includes(meta.tag)) {
    const below = fraction >= 0.5;
    return finish(
      meta.parentId,
      siblings.indexOf(row.id) + (below ? 0 : 1),
      row.id,
      below ? 'after' : 'before',
    );
  }
  if (fraction < GROUP_EDGE_SHARE) {
    return finish(meta.parentId, siblings.indexOf(row.id) + 1, row.id, 'before');
  }
  if (fraction > 1 - GROUP_EDGE_SHARE) {
    return row.expanded && row.hasChildren
      ? finish(row.id, meta.childIds.length, row.id, 'inside')
      : finish(meta.parentId, siblings.indexOf(row.id), row.id, 'after');
  }
  return finish(row.id, meta.childIds.length, row.id, 'inside');
}
